import { createSelector } from "@ngrx/store";
import { type DigitalCardsState } from "./digital-cards.interface";
import {
  DELETE_DIGITAL_CARD_PROFILE_SUCCESS,
  GET_DIGITAL_CARD_EMPLOYEE_PROFILES,
  GET_DIGITAL_CARD_EMPLOYEE_PROFILES_FAILURE,
  GET_DIGITAL_CARD_EMPLOYEE_PROFILES_SUCCESS,
  GET_DIGITAL_CARD_PROFILES,
  GET_DIGITAL_CARD_PROFILES_FAILURE,
  GET_DIGITAL_CARD_PROFILES_SUCCESS,
  SET_DEFAULT_DIGITAL_CARD_PROFILE_SUCCESS,
  type DigitalCardsActions,
} from "./digital-cards.actions";

export const initialDigitalCardsState: DigitalCardsState = {
  profiles: null,
  loading: false,
  error: undefined,
  employeeProfilesByTemplateId: {},
  loadingEmployeeTemplateId: null,
  templateReadinessById: {},
};

export function digitalCardsReducer(
  state: DigitalCardsState = initialDigitalCardsState,
  action: DigitalCardsActions,
): DigitalCardsState {
  switch (action.type) {
    case GET_DIGITAL_CARD_PROFILES:
      return {
        ...state,
        loading: true,
        error: undefined,
        employeeProfilesByTemplateId: {},
        templateReadinessById: {},
      };
    case GET_DIGITAL_CARD_PROFILES_SUCCESS:
      return {
        ...state,
        profiles: action.payload,
        loading: false,
        error: undefined,
      };
    case GET_DIGITAL_CARD_PROFILES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case GET_DIGITAL_CARD_EMPLOYEE_PROFILES:
      return {
        ...state,
        loadingEmployeeTemplateId: action.payload.templateId,
        templateReadinessById: {
          ...state.templateReadinessById,
          [action.payload.templateId]: { status: "pending" },
        },
      };
    case GET_DIGITAL_CARD_EMPLOYEE_PROFILES_SUCCESS: {
      const { templateId, profiles, readiness, expected, synced } =
        action.payload;
      return {
        ...state,
        loadingEmployeeTemplateId: readiness === "pending" ? templateId : null,
        employeeProfilesByTemplateId: {
          ...state.employeeProfilesByTemplateId,
          [templateId]: profiles,
        },
        templateReadinessById: {
          ...state.templateReadinessById,
          [templateId]: {
            status: readiness,
            expected,
            synced,
          },
        },
      };
    }
    case GET_DIGITAL_CARD_EMPLOYEE_PROFILES_FAILURE: {
      const {
        [action.payload.templateId]: _removed,
        ...templateReadinessById
      } = state.templateReadinessById;
      return {
        ...state,
        loadingEmployeeTemplateId: null,
        templateReadinessById,
        error: action.payload.error,
      };
    }
    case DELETE_DIGITAL_CARD_PROFILE_SUCCESS:
      if (!state.profiles) {
        return state;
      }
      return {
        ...state,
        profiles: state.profiles.filter((p) => p.profileId !== action.payload),
      };
    case SET_DEFAULT_DIGITAL_CARD_PROFILE_SUCCESS:
      if (!state.profiles) {
        return state;
      }
      return {
        ...state,
        profiles: state.profiles.map((p) => ({
          ...p,
          isDefault: p.profileId === action.payload,
        })),
      };
    default:
      return state;
  }
}

export const getDigitalCardsRootState = (state: {
  digitalCards: DigitalCardsState;
}): DigitalCardsState => state.digitalCards;

export const getDigitalCardsState = (s: DigitalCardsState): DigitalCardsState =>
  s;

export const getDigitalCardsProfiles = createSelector(
  getDigitalCardsRootState,
  (s) => s.profiles,
);

export const getDigitalCardsLoading = createSelector(
  getDigitalCardsRootState,
  (s) => s.loading,
);

export const getDigitalCardsLoadingEmployeeTemplateId = createSelector(
  getDigitalCardsRootState,
  (s) => s.loadingEmployeeTemplateId,
);

export const getDigitalCardsEmployeeProfilesByTemplateId = createSelector(
  getDigitalCardsRootState,
  (s) => s.employeeProfilesByTemplateId,
);

export const getDigitalCardsTemplateReadinessById = createSelector(
  getDigitalCardsRootState,
  (s) => s.templateReadinessById,
);
